import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useSearchParams, useLocation, useParams } from 'react-router-dom';
import { LogIn, Eye, EyeOff, LogOut, Home, Receipt, FolderKanban, Upload, CheckCircle2, Lock, FolderOpen, Globe, Bell, FileText, X, Sparkles, Menu, UserCircle, ChevronRight, KeyRound, Smartphone, Download, BarChart3 } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';
import ThemeToggle from '../../components/ThemeToggle';
import AppMovil, { activarAvisos, avisosActivos } from './AppMovil';
import { iconoDelEntorno } from '../../utils/identidad';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const api = axios.create({ baseURL: '/api/portal' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('portal_token');
  if (t) c.headers.Authorization = 'Bearer ' + t;
  return c;
});

/* Tema del portal: no hay sesión del CRM acá, así que se guarda en el navegador */
const leerTema = () => { try { return localStorage.getItem('crm_theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; } };
const aplicarTema = (t) => {
  document.documentElement.dataset.theme = t;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', t === 'light' ? '#ffffff' : '#050b1a');
  try { localStorage.setItem('crm_theme', t); } catch { /* modo privado */ }
};
// un link pegado sin http lo tomaría como ruta interna
const externo = (u) => { const v = String(u || '').trim(); return !v ? null : (/^https?:\/\//i.test(v) ? v : 'https://' + v.replace(/^\/+/, '')); };
const money = (v) => '$' + Number(v || 0).toLocaleString('es-AR');
const fecha = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');
const ESTADOS = {
  pendiente: { label: 'Pendiente', color: '#f59e0b' }, vencido: { label: 'Vencido', color: '#ef4444' },
  informado: { label: 'Pago informado', color: '#0ea5e9' }, pagado: { label: 'Pagado', color: '#22c55e' },
};

/* Marco de las pantallas de acceso: el ingreso a la izquierda y la presentación a la derecha */
const DEGRADE = 'linear-gradient(135deg, #0B1C33 0%, #14487F 55%, #2E7DE2 100%)';

const LO_QUE_HACE = [
  { icono: Receipt, titulo: 'Tus pagos, claros', detalle: 'Qué está al día, qué vence y el comprobante de cada factura.' },
  { icono: FolderKanban, titulo: 'El avance de tu trabajo', detalle: 'En qué etapa está tu proyecto y qué hicimos esta semana.' },
  { icono: BarChart3, titulo: 'Resultados de tus campañas', detalle: 'Inversión, resultados y la evolución mes a mes.' },
  { icono: FileText, titulo: 'Minutas y archivos', detalle: 'El resumen de cada reunión y la carpeta con todo tu material.' },
];

const esAndroid = () => /android/i.test(navigator.userAgent);
const esIphone = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

/* Cómo llevarse el portal al celular, según el teléfono que tenga */
function InstalarApp() {
  const movil = esAndroid() || esIphone();
  const yaInstalada = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  if (yaInstalada) return null;

  const caja = {
    marginTop: 22, padding: '14px 16px', borderRadius: 14,
    background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)',
  };
  const titulo = { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: '#fff' };
  const texto = { fontSize: 12.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.65, marginTop: 6 };

  if (!movil) {
    return (
      <div style={caja}>
        <div style={titulo}><Smartphone size={16} /> Tenelo en el celular</div>
        <div style={texto}>
          Abrí <b style={{ color: '#fff' }}>clientes.udream.app</b> desde tu teléfono y agregalo a la pantalla de inicio:
          queda como una app y te avisa al instante de cada novedad.
        </div>
      </div>
    );
  }

  return (
    <div style={caja}>
      <div style={titulo}><Download size={16} /> Agregalo a tu inicio</div>
      <div style={texto}>
        {esIphone() ? (
          <>Tocá <b style={{ color: '#fff' }}>Compartir</b> abajo en la pantalla y elegí <b style={{ color: '#fff' }}>“Agregar a inicio”</b>. Queda como una app y podés activar los avisos.</>
        ) : (
          <>Tocá el menú <b style={{ color: '#fff' }}>⋮</b> del navegador y elegí <b style={{ color: '#fff' }}>“Instalar aplicación”</b> o “Agregar a pantalla principal”.</>
        )}
      </div>
    </div>
  );
}

function PantallaAcceso({ titulo, bajada, children }) {
  return (
    <div className="acceso">
      {/* Ingreso */}
      <div className="acceso-form">
        <div style={{ width: '100%', maxWidth: 380 }}>
          <img className="acceso-logo-chico" src="/icon-portal-180.png" alt="Udream"
            style={{ width: 52, height: 52, borderRadius: 14, marginBottom: 20, display: 'none' }} />
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.4 }}>{titulo}</h1>
          {bajada && <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginTop: 8, marginBottom: 26, lineHeight: 1.6 }}>{bajada}</p>}
          {!bajada && <div style={{ height: 22 }} />}
          {children}
          <div className="acceso-pie" style={{ marginTop: 30, fontSize: 11, color: 'var(--text-muted)' }}>
            Udream · <a href="mailto:contacto@udream.com.ar">contacto@udream.com.ar</a>
          </div>
        </div>
      </div>

      {/* Presentación */}
      <div className="acceso-marca" style={{ background: DEGRADE }}>
        <div style={{ maxWidth: 420, width: '100%' }}>
          <img src="/icon-portal-180.png" alt="Udream"
            style={{ width: 62, height: 62, borderRadius: 17, border: '1px solid rgba(255,255,255,0.85)' }} />
          <div style={{ fontSize: 11, letterSpacing: 2.4, textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginTop: 22, fontWeight: 600 }}>
            Portal de clientes
          </div>
          <h2 style={{ fontSize: 29, fontWeight: 800, color: '#fff', lineHeight: 1.25, marginTop: 10, letterSpacing: -0.5 }}>
            Todo lo que hacemos juntos,<br />en un solo lugar
          </h2>

          <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 18 }}>
            {LO_QUE_HACE.map(f => (
              <div key={f.titulo} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff' }}>
                  <f.icono size={17} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{f.titulo}</div>
                  <div style={{ color: 'rgba(255,255,255,0.68)', fontSize: 12.5, lineHeight: 1.6, marginTop: 2 }}>{f.detalle}</div>
                </div>
              </div>
            ))}
          </div>

          <InstalarApp />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Login ---------------- */
function Login({ onEntrar }) {
  const [form, setForm] = useState({ email: '', password: '' });
  const [ver, setVer] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [modo, setModo] = useState('clave');     // clave | codigo | esperando
  const [codigo, setCodigo] = useState('');

  const entrar = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const r = await api.post('/login', form);
      localStorage.setItem('portal_token', r.data.token);
      onEntrar(r.data.usuario);
    } catch (err) { toast.error(err.response?.data?.error || 'No pudimos entrar'); }
    finally { setCargando(false); }
  };

  // Enlace de recuperación, para quien prefiere elegir una contraseña nueva
  const recuperar = async () => {
    if (!form.email) return toast.error('Poné tu email y volvé a tocar');
    try {
      await api.post('/olvide', { email: form.email });
      toast.success('Si ese email tiene acceso, te llega el enlace para crear una contraseña nueva', { duration: 7000 });
    } catch { toast.error('No se pudo enviar'); }
  };

  // Entrar con un código de seis dígitos que llega al mail
  const pedirCodigo = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error('Poné tu email');
    setCargando(true);
    try {
      await api.post('/codigo', { email: form.email });
      setModo('esperando');
      toast.success('Si ese email tiene acceso, te llega el código en un minuto', { duration: 6000 });
    } catch { toast.error('No se pudo enviar'); }
    finally { setCargando(false); }
  };

  const entrarConCodigo = async (e) => {
    e.preventDefault();
    setCargando(true);
    try {
      const r = await api.post('/codigo/entrar', { email: form.email, codigo });
      localStorage.setItem('portal_token', r.data.token);
      onEntrar(r.data.usuario);
    } catch (err) { toast.error(err.response?.data?.error || 'No pudimos entrar'); }
    finally { setCargando(false); }
  };

  const campoEmail = (
    <div className="form-group"><label>Email</label>
      <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required autoFocus /></div>
  );

  if (modo === 'esperando') {
    return (
      <PantallaAcceso titulo="Revisá tu mail" bajada={`Te mandamos un código de 6 dígitos a ${form.email}. Vence en 15 minutos.`}>
        <form onSubmit={entrarConCodigo}>
          <div className="form-group"><label>Código</label>
            <input value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric" autoFocus placeholder="000000" maxLength={6}
              style={{ fontSize: 24, letterSpacing: 8, textAlign: 'center', fontWeight: 700 }} /></div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }}
            type="submit" disabled={cargando || codigo.length < 6}>{cargando ? 'Entrando...' : 'Entrar'}</button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
            onClick={pedirCodigo} disabled={cargando}>Mandame otro código</button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center' }}
            onClick={() => { setModo('clave'); setCodigo(''); }}>Entrar con contraseña</button>
        </form>
      </PantallaAcceso>
    );
  }

  if (modo === 'codigo') {
    return (
      <PantallaAcceso titulo="Entrar con un código"
        bajada="Te mandamos un código al mail y entrás sin contraseña. Sirve también si no la recordás.">
        <form onSubmit={pedirCodigo}>
          {campoEmail}
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} type="submit" disabled={cargando}>
            {cargando ? 'Enviando...' : 'Mandarme el código'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
            onClick={() => setModo('clave')}>Entrar con contraseña</button>
        </form>
      </PantallaAcceso>
    );
  }

  return (
    <PantallaAcceso titulo="Entrá a tu portal" bajada="Tus servicios, facturas y el avance de tus proyectos, en un solo lugar.">
      <form onSubmit={entrar}>
        {campoEmail}
        <div className="form-group"><label>Contraseña</label>
          <div style={{ position: 'relative' }}>
            <input type={ver ? 'text' : 'password'} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required style={{ paddingRight: 42 }} />
            <button type="button" onClick={() => setVer(!ver)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
              {ver ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} disabled={cargando} type="submit">
          <LogIn size={16} /> {cargando ? 'Entrando...' : 'Entrar'}
        </button>
      </form>

      <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
        onClick={recuperar}>Olvidé mi contraseña</button>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 12px' }}>
        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>o</span>
        <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      </div>
      <button type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', padding: 11 }}
        onClick={() => setModo('codigo')}>
        <KeyRound size={15} /> Entrar con un código al mail
      </button>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8 }}>
        Si no recordás tu contraseña, esta es la forma más rápida
      </div>
    </PantallaAcceso>
  );
}

/* ---------------- Recuperar contraseña ---------------- */
function Recuperar() {
  const [params] = useSearchParams();
  const [pass, setPass] = useState({ a: '', b: '' });
  const [listo, setListo] = useState(false);
  const navigate = useNavigate();
  const guardar = async (e) => {
    e.preventDefault();
    if (pass.a !== pass.b) return toast.error('Las contraseñas no coinciden');
    try { await api.post('/recuperar', { token: params.get('token'), password: pass.a }); setListo(true); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <PantallaAcceso titulo={listo ? 'Contraseña actualizada' : 'Creá tu contraseña'}
      bajada={listo ? 'Ya podés entrar al portal con la contraseña nueva.' : 'Elegí una contraseña de al menos 8 caracteres.'}>
      {listo ? (
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={() => navigate('/', { replace: true })}>
          <LogIn size={16} /> Ir al portal
        </button>
      ) : (
        <form onSubmit={guardar}>
          <div className="form-group"><label>Contraseña</label><input type="password" value={pass.a} onChange={e => setPass({ ...pass, a: e.target.value })} required minLength={8} autoFocus /></div>
          <div className="form-group"><label>Repetir contraseña</label><input type="password" value={pass.b} onChange={e => setPass({ ...pass, b: e.target.value })} required /></div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} type="submit">Guardar</button>
        </form>
      )}
    </PantallaAcceso>
  );
}

/* Los pagos se ven como tabla en la compu y como tarjetas en el celular */
function ListaPagos({ cobros, onPagar, mostrarCliente }) {
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  const Factura = ({ c }) => (c.archivo_url ? (
    <a href={externo(c.archivo_url)} target="_blank" rel="noopener noreferrer"
      style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
      <FileText size={11} /> Factura{c.numero ? ` ${c.numero}` : ''}
    </a>
  ) : null);

  const Accion = ({ c }) => {
    if (!c.habilitado) {
      return <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Lock size={13} /> Todavía no</span>;
    }
    if (['pagado', 'informado'].includes(c.estado)) return null;
    return <button className="btn btn-primary btn-sm" onClick={() => onPagar(c)}>Ya pagué</button>;
  };

  return (
    <>
      {/* compu */}
      <div className="solo-pc table-container">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>Concepto</th><th style={th}>Vence</th>
            <th style={{ ...th, textAlign: 'right' }}>Monto</th><th style={th}>Estado</th><th style={th}></th></tr></thead>
          <tbody>{cobros.map(c => {
            const e = ESTADOS[c.estado] || {};
            return (
              <tr key={c.id} style={{ opacity: c.habilitado ? 1 : 0.6 }}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{c.concepto}{c.cuota_total ? ` (${c.cuota_numero}/${c.cuota_total})` : ''}</div>
                  {mostrarCliente && c.cliente && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.cliente}</div>}
                  <Factura c={c} />
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{c.habilitado ? fecha(c.vencimiento) : '—'}</td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(c.monto)}</td>
                <td style={{ ...td, color: c.habilitado ? e.color : 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}>
                  {c.habilitado ? e.label : 'No disponible'}</td>
                <td style={{ ...td, textAlign: 'right' }}><Accion c={c} /></td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>

      {/* celular */}
      <div className="solo-movil">
        {cobros.map(c => {
          const e = ESTADOS[c.estado] || {};
          return (
            <div key={c.id} style={{ borderTop: '1px solid var(--border)', padding: '14px 0', opacity: c.habilitado ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{c.concepto}{c.cuota_total ? ` (${c.cuota_numero}/${c.cuota_total})` : ''}</div>
                  <div style={{ fontSize: 11, color: c.habilitado ? e.color : 'var(--text-muted)', marginTop: 2 }}>
                    {c.habilitado ? `${e.label} · vence ${fecha(c.vencimiento)}` : 'Todavía no disponible'}
                  </div>
                  <Factura c={c} />
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, whiteSpace: 'nowrap' }}>{money(c.monto)}</div>
              </div>
              <div style={{ marginTop: 10 }}><Accion c={c} /></div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ---------------- Inicio ---------------- */
const Acceso = ({ icono: Icono, titulo, valor, detalle, onClick, color }) => (
  <div className="card" style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center' }} onClick={onClick}>
    <div style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--accent-glow)', color: color || 'var(--accent)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icono size={19} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{titulo}</div>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2, color: color || 'var(--text-primary)' }}>{valor}</div>
      {detalle && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{detalle}</div>}
    </div>
  </div>
);

function Inicio({ onPagar }) {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const cargar = useCallback(() => { api.get('/inicio').then(r => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { cargar(); }, [cargar]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;

  const card = { marginBottom: 18 };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const cobrables = (d.cobros || []).filter(c => c.habilitado && c.estado !== 'informado');
  const aPagar = cobrables.reduce((a, c) => a + Number(c.monto || 0), 0);
  const servicios = (d.contratos || []).length;
  const proyectos = (d.proyectos || []).length;
  const ultimaMinuta = (d.minutas || [])[0];

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Hola{d.usuario.nombre ? ', ' + d.usuario.nombre.split(' ')[0] : ''}</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>{d.usuario.cliente}</p>

      {/* Accesos rápidos */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 22 }}>
        {d.usuario.ve_importes && (
          <Acceso icono={Receipt} titulo="Para pagar" valor={aPagar ? money(aPagar) : 'Al día'} color={aPagar ? '#f59e0b' : '#22c55e'}
            detalle={cobrables.length ? `${cobrables.length} pago${cobrables.length === 1 ? '' : 's'} disponible${cobrables.length === 1 ? '' : 's'}` : 'No tenés pagos pendientes'}
            onClick={() => navigate('/facturas')} />
        )}
        <Acceso icono={FolderKanban} titulo="Mis servicios" valor={servicios + proyectos}
          detalle={`${proyectos} proyecto${proyectos === 1 ? '' : 's'} · ${servicios} servicio${servicios === 1 ? '' : 's'}`}
          onClick={() => navigate('/proyectos')} />
        <Acceso icono={FileText} titulo="Minutas" valor={(d.minutas || []).length || '—'}
          detalle={ultimaMinuta ? `Última: ${fecha(ultimaMinuta.fecha)}` : 'Todavía no hay reuniones cargadas'}
          onClick={() => navigate('/minutas')} />
        <Acceso icono={Bell} titulo="Avisos" valor={d.sin_leer || 0} color={d.sin_leer ? 'var(--accent)' : undefined}
          detalle={d.sin_leer ? 'sin leer' : 'Estás al día'} onClick={() => navigate('/notificaciones')} />
      </div>

      {/* Pagos */}
      {d.usuario.ve_importes && d.cobros.length > 0 && (
        <div className="card" style={card}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Pagos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            Los que están en gris todavía no están habilitados: te vamos a avisar cuando se puedan abonar.
          </div>
          <ListaPagos cobros={d.cobros} onPagar={onPagar} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, alignItems: 'stretch' }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Últimas novedades</h3>
            <span style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/proyectos')}>Ver todo</span>
          </div>
          {(d.avances || []).length ? d.avances.map(a => (
            <div key={a.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }}
              onClick={() => navigate(a.entidad === 'proyecto' ? `/proyectos/${a.entidad_id}` : `/servicios/${a.entidad_id}`)}>
              <div style={{ fontWeight: 600 }}>{a.titulo}</div>
              {a.detalle && <div style={{ color: 'var(--text-secondary)', marginTop: 2 }}>{a.detalle}</div>}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{a.origen} · {fecha(a.fecha)}</div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Cuando avancemos con tus trabajos, lo vas a ver acá.</p>}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700 }}>Minutas de reuniones</h3>
            {(d.minutas || []).length > 0 && <span style={{ fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate('/minutas')}>Ver todas</span>}
          </div>
          {(d.minutas || []).length ? d.minutas.slice(0, 3).map(m => (
            <div key={m.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer' }} onClick={() => navigate('/minutas')}>
              <div style={{ fontWeight: 600 }}>{m.titulo}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fecha(m.fecha)}</div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Acá vas a ver el resumen de nuestras reuniones.</p>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Tus servicios</h3>
          {d.contratos.length ? d.contratos.map(c => (
            <div key={c.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border)', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}
              onClick={() => navigate('/servicios/' + c.id)}>
              <div><div style={{ fontWeight: 600 }}>{c.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {c.tipo === 'abono' ? 'Abono mensual' : c.tipo === 'anual' ? `Renovación anual · vence ${fecha(c.proximo_vencimiento)}` : 'Proyecto'}
                  {d.usuario.es_agencia && c.cliente !== d.usuario.cliente ? ` · ${c.cliente}` : ''}
                </div></div>
              {d.usuario.ve_importes && c.monto && <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(c.monto)}</div>}
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no tenés servicios cargados.</p>}
        </div>
      </div>

      <Sugerencia contexto="inicio" />
    </div>
  );
}

/* Todas las minutas */
function Minutas() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/minutas').then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Minutas' }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Minutas de reuniones</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>El resumen de lo que hablamos en cada encuentro.</p>
      {rows.length ? rows.map(m => (
        <details key={m.id} className="card" style={{ marginBottom: 12 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
            {m.titulo}<span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 12 }}> · {fecha(m.fecha)}</span>
          </summary>
          {m.detalle && <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 12, fontSize: 14, lineHeight: 1.7 }}>{m.detalle}</div>}
        </details>
      )) : <p style={{ color: 'var(--text-muted)' }}>Todavía no hay minutas cargadas.</p>}
    </div>
  );
}

/* ---------------- Facturas ---------------- */
function Facturas({ onPagar }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/facturas').then(r => setRows(r.data)).catch(() => {}); }, []);
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Facturas y pagos' }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 18 }}>Facturas y pagos</h1>
      <div className="card">
        {rows.length ? <ListaPagos cobros={rows} onPagar={onPagar} mostrarCliente />
          : <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no hay movimientos.</p>}
      </div>
    </div>
  );
}

/* ---------------- Proyectos ---------------- */
const ESTADO_PROY = {
  planificacion: { label: 'En planificación', color: '#f59e0b' }, en_desarrollo: { label: 'En desarrollo', color: '#0ea5e9' },
  revision: { label: 'En revisión', color: '#a855f7' }, publicacion: { label: 'En publicación', color: '#14b8a6' },
  terminado: { label: 'Terminado', color: '#22c55e' }, cancelado: { label: 'Cancelado', color: '#ef4444' },
};
const TIPO_MOV = { minuta: 'Minuta', mantenimiento: 'Mantenimiento', reporte: 'Reporte', ajuste: 'Ajuste', entrega: 'Entrega', avance: '', nota: '' };

/* Migas: dónde estoy y cómo vuelvo */
function Migas({ camino }) {
  const navegar = useNavigate();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14, fontSize: 12.5 }}>
      {camino.map((paso, i) => {
        const ultimo = i === camino.length - 1;
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {ultimo
              ? <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{paso.label}</span>
              : <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navegar(paso.to)}>{paso.label}</span>}
            {!ultimo && <ChevronRight size={13} style={{ color: 'var(--text-muted)' }} />}
          </span>
        );
      })}
    </div>
  );
}

/* Proyectos y servicios en una sola pantalla: arriba los trabajos, abajo lo que está contratado */
function Trabajos() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/trabajos').then(r => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;

  const fila = { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' };
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Mis servicios' }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Mis servicios</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>Entrá a cada uno para ver en qué anda, su historial y sus archivos.</p>

      {d.proyectos.length > 0 && (<>
        <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>Desarrollos y proyectos</h3>
        {d.proyectos.map(p => {
          const e = ESTADO_PROY[p.estado] || {};
          return (
            <div key={p.id} className="card" style={{ marginBottom: 12, cursor: 'pointer' }} onClick={() => navigate('/proyectos/' + p.id)}>
              <div style={fila}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{p.nombre}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {p.fecha_inicio ? `Desde ${fecha(p.fecha_inicio)}` : ''}{p.movimientos ? ` · ${p.movimientos} novedades` : ''}
                  </div>
                </div>
                <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', color: e.color }}>{e.label || p.estado}</span>
              </div>
            </div>
          );
        })}
      </>)}

      {d.servicios.length > 0 && (<>
        <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-muted)', margin: '22px 0 10px' }}>Servicios contratados</h3>
        {d.servicios.map(s => (
          <div key={s.id} className="card" style={{ marginBottom: 12, cursor: 'pointer' }} onClick={() => navigate('/servicios/' + s.id)}>
            <div style={fila}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{s.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {s.tipo === 'abono' ? 'Abono mensual' : `Renovación anual · vence ${fecha(s.proximo_vencimiento)}`}
                  {s.movimientos ? ` · ${s.movimientos} novedades` : ''}
                </div>
                {s.plataformas && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                    {s.plataformas.split(',').map(x => x.trim()).filter(Boolean).map(x => (
                      <span key={x} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)', fontSize: 11 }}>{x}</span>
                    ))}
                  </div>
                )}
              </div>
              {s.monto ? <div style={{ fontWeight: 700 }}>{money(s.monto)}</div> : null}
            </div>
          </div>
        ))}
      </>)}

      {!d.proyectos.length && !d.servicios.length && <p style={{ color: 'var(--text-muted)' }}>Todavía no tenés proyectos ni servicios cargados.</p>}

      <div style={{ marginTop: 24 }}><Sugerencia contexto="servicios" /></div>
    </div>
  );
}

/* Historial compartido por proyectos y servicios */
function Historial({ movimientos }) {
  if (!movimientos.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Todavía no hay novedades cargadas.</p>;
  return movimientos.map(m => (
    <div key={m.id} style={{ display: 'flex', gap: 12, paddingBottom: 18 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--accent)', marginTop: 5, flexShrink: 0 }} />
        <span style={{ flex: 1, width: 1, background: 'var(--border)' }} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fecha(m.fecha)}{TIPO_MOV[m.tipo] ? ` · ${TIPO_MOV[m.tipo]}` : ''}</div>
        <div style={{ fontWeight: 600, fontSize: 14, marginTop: 2 }}>{m.titulo}</div>
        {m.detalle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 4, lineHeight: 1.6 }}>{m.detalle}</div>}
        {m.archivo_url && <a href={externo(m.archivo_url)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>Abrir enlace</a>}
      </div>
    </div>
  ));
}

function DetalleProyecto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/proyectos/' + id).then(r => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const p = d.proyecto;
  const e = ESTADO_PROY[p.estado] || {};
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Mis servicios', to: '/proyectos' }, { label: p.nombre }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>{p.nombre}</h1>
        <span className="badge" style={{ background: 'transparent', border: '1px solid var(--border)', color: e.color }}>{e.label || p.estado}</span>
      </div>
      {p.resumen && <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 14, lineHeight: 1.6 }}>{p.resumen}</p>}
      {p.drive_url && (
        <a className="btn btn-secondary btn-sm" href={externo(p.drive_url)} target="_blank" rel="noopener noreferrer" style={{ marginBottom: 18 }}>
          <FolderOpen size={15} /> Carpeta de archivos
        </a>
      )}

      <div className="card" style={{ marginTop: 18, marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Historial</h3>
        <Historial movimientos={d.movimientos} />
      </div>

      {d.cobros.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Pagos</h3>
          {d.cobros.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 13, opacity: c.habilitado ? 1 : 0.55 }}>
              <span>{c.concepto}
                <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {c.habilitado ? (ESTADOS[c.estado] || {}).label : 'todavía no disponible'}</span>
              </span>
              <b>{money(c.monto)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MESES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const periodoCorto = (p) => { const [a, m] = String(p || '').split('-'); return `${MESES_CORTO[Number(m) - 1] || ''} ${String(a).slice(2)}`; };
const periodoLargo = (p) => {
  const largos = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const [a, m] = String(p || '').split('-');
  return `${largos[Number(m) - 1] || ''} ${a || ''}`;
};
const sumar = (plats, campo) => (plats || []).reduce((a, x) => a + Number(x[campo] || 0), 0);

/* Reportes de campañas: los números del mes y la evolución */
function Reportes({ reportes }) {
  const [abierto, setAbierto] = useState(reportes[0] ? reportes[0].id : null);
  if (!reportes.length) return null;

  const serie = [...reportes].reverse().map(r => {
    const plats = (r.metricas || {}).plataformas || [];
    const inversion = sumar(plats, 'inversion');
    const resultados = sumar(plats, 'resultados');
    return { mes: periodoCorto(r.periodo), inversion, resultados, costo: resultados ? Math.round(inversion / resultados) : null };
  });
  const ultimo = serie[serie.length - 1] || {};
  const previo = serie[serie.length - 2];
  const variacion = (campo, mejorSiBaja) => {
    if (!previo || !previo[campo] || !ultimo[campo]) return null;
    const pct = Math.round(((ultimo[campo] / previo[campo]) - 1) * 1000) / 10;
    return { pct, bueno: mejorSiBaja ? pct <= 0 : pct >= 0 };
  };
  const actual = reportes.find(r => r.id === abierto) || reportes[0];
  const plats = (actual.metricas || {}).plataformas || [];
  const destacados = (actual.metricas || {}).destacados || [];
  const dato = { fontSize: 12, color: 'var(--text-muted)' };
  const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };
  const tick = { fill: 'var(--chart-text)', fontSize: 11 };
  const Variacion = ({ v }) => !v ? null : (
    <span style={{ fontSize: 12, fontWeight: 700, color: v.bueno ? '#22c55e' : '#ef4444' }}> {v.pct > 0 ? '+' : ''}{v.pct}%</span>
  );

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700 }}>Resultados</h3>
        <select value={abierto || ''} onChange={e => setAbierto(Number(e.target.value))} style={{ width: 'auto', fontSize: 13 }}>
          {reportes.map(r => <option key={r.id} value={r.id}>{periodoLargo(r.periodo)}</option>)}
        </select>
      </div>

      {actual.conclusion && (
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, whiteSpace: 'pre-line', margin: '10px 0 16px' }}>{actual.conclusion}</div>
      )}

      {destacados.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
          {destacados.map((x, i) => (
            <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px' }}>
              <div style={dato}>{x.label}</div>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{x.valor}
                {x.variacion && <span style={{ fontSize: 12, fontWeight: 700, color: String(x.variacion).startsWith('-') ? '#ef4444' : '#22c55e' }}> {x.variacion}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {plats.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: 18 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 420 }}>
            <thead><tr>
              {['Plataforma', 'Inversión', 'Impresiones', 'Clics', 'Resultados'].map(h => (
                <th key={h} style={{ textAlign: h === 'Plataforma' ? 'left' : 'right', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '6px 8px' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>{plats.map((x, i) => (
              <tr key={i}>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>{x.nombre}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{x.inversion ? money(x.inversion) : '—'}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{Number(x.impresiones || x.alcance || 0).toLocaleString('es-AR') || '—'}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right' }}>{Number(x.clics || 0).toLocaleString('es-AR')}</td>
                <td style={{ padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13, textAlign: 'right', fontWeight: 700 }}>{Number(x.resultados || 0).toLocaleString('es-AR')}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 18 }}>
        <div><div style={dato}>Inversión del mes</div><div style={{ fontSize: 19, fontWeight: 800 }}>{money(ultimo.inversion)}<Variacion v={variacion('inversion')} /></div></div>
        <div><div style={dato}>Resultados</div><div style={{ fontSize: 19, fontWeight: 800 }}>{ultimo.resultados || '—'}<Variacion v={variacion('resultados')} /></div></div>
        <div><div style={dato}>Costo por resultado</div><div style={{ fontSize: 19, fontWeight: 800 }}>{ultimo.costo ? money(ultimo.costo) : '—'}<Variacion v={variacion('costo', true)} /></div></div>
      </div>

      {serie.length > 1 && (<>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Inversión y resultados</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={serie} margin={{ left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="mes" tick={tick} />
            <YAxis yAxisId="i" tick={tick} tickFormatter={v => '$' + Math.round(v / 1000) + 'k'} width={58} />
            <YAxis yAxisId="r" orientation="right" tick={tick} width={34} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [n === 'Inversión' ? money(v) : v, n]} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--chart-text)' }} />
            <Bar yAxisId="i" dataKey="inversion" name="Inversión" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            <Bar yAxisId="r" dataKey="resultados" name="Resultados" fill="#22c55e" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>

        {serie.some(s => s.costo) && (<>
          <div style={{ fontSize: 13, fontWeight: 700, margin: '16px 0 8px' }}>Costo por resultado</div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={serie} margin={{ left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="mes" tick={tick} />
              <YAxis tick={tick} tickFormatter={v => '$' + Math.round(v / 1000) + 'k'} width={58} />
              <Tooltip contentStyle={tooltipStyle} formatter={v => money(v)} />
              <Line type="monotone" dataKey="costo" name="Costo por resultado" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </>)}
      </>)}

      {actual.documento_url && (
        <a className="btn btn-secondary btn-sm" href={externo(actual.documento_url)} target="_blank" rel="noopener noreferrer" style={{ marginTop: 16 }}>
          <FileText size={15} /> Ver el informe de {periodoLargo(actual.periodo)}
        </a>
      )}
    </div>
  );
}

function DetalleServicio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/servicios/' + id).then(r => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  const s = d.servicio;
  const dato = { fontSize: 12, color: 'var(--text-muted)' };
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Mis servicios', to: '/proyectos' }, { label: s.nombre }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{s.nombre}</h1>
      {s.plataformas && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {s.plataformas.split(',').map(x => x.trim()).filter(Boolean).map(x => (
            <span key={x} className="badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>{x}</span>
          ))}
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
          <div><div style={dato}>Estado</div><div style={{ fontWeight: 700, color: s.estado === 'activo' ? '#22c55e' : 'var(--text-primary)' }}>
            {s.estado === 'activo' ? 'Activo' : s.estado === 'pausado' ? 'Pausado' : 'Dado de baja'}</div></div>
          <div><div style={dato}>Modalidad</div><div style={{ fontWeight: 700 }}>{s.tipo === 'abono' ? 'Abono mensual' : s.tipo === 'anual' ? 'Renovación anual' : 'Pago único'}</div></div>
          {s.proximo_vencimiento && <div><div style={dato}>Próxima renovación</div><div style={{ fontWeight: 700 }}>{fecha(s.proximo_vencimiento)}</div></div>}
          {s.monto ? <div><div style={dato}>Importe</div><div style={{ fontWeight: 700 }}>{money(s.monto)}</div></div> : null}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          {s.web_url && <a className="btn btn-secondary btn-sm" href={externo(s.web_url)} target="_blank" rel="noopener noreferrer"><Globe size={15} /> Ver el sitio</a>}
          {s.drive_url && <a className="btn btn-secondary btn-sm" href={externo(s.drive_url)} target="_blank" rel="noopener noreferrer"><FolderOpen size={15} /> Archivos y facturas</a>}
        </div>
      </div>

      {(d.reportes || []).length > 0 && <Reportes reportes={d.reportes} />}

      {s.panel_url && (
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Tus datos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Información actualizada de tus campañas.</div>
          <div style={{ position: 'relative', width: '100%', paddingTop: '75%', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <iframe title="Panel de datos" src={s.panel_url} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Actividad</h3>
        <Historial movimientos={d.movimientos} />
      </div>

      <Sugerencia contexto="servicio" />

      {d.cobros.length > 0 && (
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Últimos cobros</h3>
          {d.cobros.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderTop: '1px solid var(--border)', fontSize: 13, opacity: c.habilitado ? 1 : 0.55 }}>
              <span>{c.concepto}<span style={{ color: 'var(--text-muted)', fontSize: 11 }}> · {fecha(c.vencimiento)}</span></span>
              <b>{money(c.monto)}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Una sugerencia, con su motivo. Si no encaja ninguna, no se muestra nada. */
function Sugerencia({ contexto }) {
  const [s, setS] = useState(null);
  const [modal, setModal] = useState(false);
  const navegar = useNavigate();
  useEffect(() => { api.get('/sugerencias', { params: { limite: 1 } }).then(r => setS(r.data[0] || null)).catch(() => {}); }, []);
  if (!s) return null;

  const descartar = () => { api.post(`/sugerencias/${s.slug}/descartar`).catch(() => {}); setS(null); };
  return (
    <>
      <div className="card" style={{ marginTop: contexto === 'inicio' ? 18 : 0, marginBottom: 18, borderLeft: '3px solid var(--accent)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              Algo que puede sumarte
            </div>
            <div style={{ fontWeight: 700, fontSize: 15, marginTop: 6 }}>{s.nombre}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.6 }}>{s.gancho || s.descripcion}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>{s.razon}</div>
          </div>
          <button onClick={descartar} title="No me interesa"
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary btn-sm" onClick={() => setModal(true)}>Me interesa</button>
          <button className="btn btn-ghost btn-sm" onClick={descartar}>Ahora no</button>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--accent)', cursor: 'pointer' }}
            onClick={() => navegar('/servicios')}>Ver todo lo que hacemos →</span>
        </div>
      </div>
      {modal && <ModalConsulta servicio={s} onClose={(pedido) => { setModal(false); if (pedido) setS(null); }} />}
    </>
  );
}

/* "Me interesa": no es un formulario público, es una consulta que nos llega a nosotros */
function ModalConsulta({ servicio, onClose }) {
  const [mensaje, setMensaje] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await api.post('/consultas', { servicio_slug: servicio.slug, mensaje });
      toast.success('Listo, te escribimos en las próximas 24 horas', { duration: 6000 });
      onClose(true);
    } catch { toast.error('No se pudo enviar'); setEnviando(false); }
  };
  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <h2>{servicio.nombre}</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>{servicio.descripcion}</p>
        <form onSubmit={enviar}>
          <div className="form-group"><label>¿Querés contarnos algo? (opcional)</label>
            <textarea rows={3} value={mensaje} onChange={e => setMensaje(e.target.value)}
              style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder="Lo que tengas en mente" /></div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
            Te contactamos nosotros para charlarlo. No se contrata nada desde acá.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={enviando}>{enviando ? 'Enviando...' : 'Quiero que me contacten'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


/* Mi cuenta: los datos del usuario, los de facturación y la contraseña */
function MiCuenta({ onCambiarPass, onSalir }) {
  const [d, setD] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { api.get('/mi-cuenta').then(r => setD(r.data)).catch(() => toast.error('No se pudo cargar')); }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;

  const setU = (k) => (e) => setD({ ...d, usuario: { ...d.usuario, [k]: e.target.value } });
  const setC = (k) => (e) => setD({ ...d, cliente: { ...d.cliente, [k]: e.target.value } });

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.put('/mi-cuenta', { usuario: d.usuario, cliente: d.ve_importes ? d.cliente : undefined });
      toast.success('Datos guardados');
    } catch { toast.error('No se pudo guardar'); } finally { setGuardando(false); }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Mi cuenta' }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Mi cuenta</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Tus datos y los de facturación. Si algo cambia, actualizalo acá y lo tomamos para las próximas facturas.
      </p>

      <form onSubmit={guardar}>
        <div className="card" style={{ marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Tu marca</h3>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
            <img src={d.cliente.logo_url || iconoDelEntorno()} alt="" onError={e => { e.target.src = iconoDelEntorno(); }}
              style={{ width: 64, height: 64, borderRadius: 16, objectFit: 'cover', background: '#fff', border: '1px solid var(--border)' }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Subí tu logo y lo vas a ver en todo el portal. Si no cargás ninguno, usamos el nuestro.
              </div>
              <label className="btn btn-secondary btn-sm" style={{ marginTop: 8, cursor: 'pointer' }}>
                Cambiar logo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (ev) => {
                  const f = ev.target.files[0];
                  if (!f) return;
                  const fd = new FormData(); fd.append('logo', f);
                  try {
                    const { data } = await api.post('/mi-cuenta/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    setD({ ...d, cliente: { ...d.cliente, logo_url: data.logo_url } });
                    toast.success('Logo actualizado');
                  } catch { toast.error('No se pudo subir'); }
                }} />
              </label>
            </div>
          </div>

          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>Tus datos</h3>
          <div className="grilla-cuenta" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 16px' }}>
            <div className="form-group"><label>Nombre</label>
              <input value={d.usuario.nombre || ''} onChange={setU('nombre')} /></div>
            <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Email</label>
              <input value={d.usuario.email || ''} disabled style={{ opacity: 0.7 }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Es tu usuario: para cambiarlo, escribinos</div></div>
            <div className="form-group"><label>Teléfono</label>
              <input value={d.usuario.telefono || ''} onChange={setU('telefono')} placeholder="Para contactarte" /></div>
            <div className="form-group"><label>Cargo</label>
              <input value={d.usuario.cargo || ''} onChange={setU('cargo')} placeholder="Dueño, marketing, administración..." /></div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCambiarPass}><Lock size={14} /> Cambiar contraseña</button>
        </div>

        <div className="card" style={{ marginBottom: 18 }} id="avisos">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Avisos</h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
            Te recomendamos dejarlos activados: es la forma de enterarte al momento de un pago disponible,
            un avance de tus proyectos o un reporte nuevo. En el portal los vas a ver igual.
          </div>
          <Avisos />
        </div>

        {d.ve_importes && (
          <div className="card" style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Datos de facturación</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Con estos datos emitimos tus facturas.</div>
            <div className="grilla-cuenta" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0 16px' }}>
              <div className="form-group"><label>Razón social</label>
                <input value={d.cliente.razon_social || ''} onChange={setC('razon_social')} placeholder={d.cliente.nombre} /></div>
              <div className="form-group"><label>CUIT</label>
                <input value={d.cliente.cuit || ''} onChange={setC('cuit')} placeholder="30-00000000-0" /></div>
              <div className="form-group"><label>Condición frente al IVA</label>
                <select value={d.cliente.condicion_iva || ''} onChange={setC('condicion_iva')}>
                  <option value="">—</option>
                  <option value="responsable_inscripto">Responsable inscripto</option>
                  <option value="monotributo">Monotributo</option>
                  <option value="exento">Exento</option>
                  <option value="consumidor_final">Consumidor final</option>
                </select></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Email para facturas</label>
                <input type="email" value={d.cliente.email_facturacion || ''} onChange={setC('email_facturacion')} /></div>
              <div className="form-group"><label>Teléfono</label>
                <input value={d.cliente.telefono || ''} onChange={setC('telefono')} /></div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Dirección</label>
                <input value={d.cliente.direccion || ''} onChange={setC('direccion')} /></div>
              <div className="form-group"><label>Ciudad</label>
                <input value={d.cliente.ciudad || ''} onChange={setC('ciudad')} /></div>
              <div className="form-group"><label>Sitio web</label>
                <input value={d.cliente.web || ''} onChange={setC('web')} placeholder="https://" /></div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando...' : 'Guardar cambios'}</button>
          <button type="button" className="btn btn-ghost" style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} onClick={onSalir}>
            <LogOut size={15} /> Cerrar sesión
          </button>
        </div>
      </form>
    </div>
  );
}

/* Todo lo que hacemos, agrupado por objetivo */
const OBJETIVOS = [
  { key: 'vender', label: 'Para vender más' },
  { key: 'atender', label: 'Para atender mejor' },
  { key: 'automatizar', label: 'Para automatizar tareas' },
];
function Servicios() {
  const [rows, setRows] = useState(null);
  const [elegido, setElegido] = useState(null);
  useEffect(() => { api.get('/servicios-catalogo').then(r => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Lo que hacemos' }]} />
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Lo que hacemos</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 22 }}>
        Si algo te sirve, tocá "Me interesa" y te contactamos para charlarlo.
      </p>
      {OBJETIVOS.map(o => {
        const items = rows.filter(r => r.objetivo === o.key);
        if (!items.length) return null;
        return (
          <div key={o.key} style={{ marginBottom: 26 }}>
            <h3 style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>{o.label}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
              {items.map(s => (
                <div key={s.slug} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{s.nombre}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, flex: 1 }}>{s.descripcion}</div>
                  {s.consultado
                    ? <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Ya lo consultaste, te escribimos pronto</span>
                    : <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setElegido(s)}>Me interesa</button>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {elegido && <ModalConsulta servicio={elegido} onClose={(p) => {
        setElegido(null);
        if (p) setRows(rows.map(r => (r.slug === elegido.slug ? { ...r, consultado: true } : r)));
      }} />}
    </div>
  );
}

/* Qué avisos recibe por mail y si tiene el celular enlazado */
function Avisos() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get('/avisos/preferencias').then(r => setP(r.data)).catch(() => {}); }, []);
  if (!p) return <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Cargando...</div>;

  const cambiar = async (campo, valor) => {
    setP({ ...p, [campo]: valor });
    try { await api.put('/avisos/preferencias', { [campo]: valor }); }
    catch { toast.error('No se pudo guardar'); setP({ ...p, [campo]: !valor }); }
  };

  const fila = (campo, titulo, detalle) => (
    <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '11px 0', borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
      <input type="checkbox" checked={!!p[campo]} onChange={e => cambiar(campo, e.target.checked)} style={{ width: 17, height: 17, marginTop: 2 }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{titulo}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{detalle}</span>
      </span>
    </label>
  );

  return (
    <div>
      {p.ve_importes && fila('pagos', 'Pagos y facturas por mail', 'Cuando hay un pago disponible, cuando vence y cuando lo confirmamos.')}
      {fila('novedades', 'Novedades por mail', 'Avances de tus proyectos, minutas y reportes nuevos.')}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
        <b>En el celular:</b>{' '}
        {p.dispositivos > 0
          ? <span style={{ color: '#22c55e' }}>activado en {p.dispositivos} {p.dispositivos === 1 ? 'dispositivo' : 'dispositivos'}</span>
          : <BotonAvisos />}
      </div>
      {(!p.pagos || !p.novedades) && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#f59e0b', lineHeight: 1.6 }}>
          Con los avisos apagados podés enterarte tarde de un vencimiento. Los seguís viendo en el portal cuando entres.
        </div>
      )}
    </div>
  );
}

/* Campanita: muestra el contador y lleva a la sección de avisos */
function Campanita() {
  const [sinLeer, setSinLeer] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const cargar = () => api.get('/notificaciones').then(r => setSinLeer(r.data.sin_leer)).catch(() => {});
    cargar();
    const t = setInterval(cargar, 120000);
    return () => clearInterval(t);
  }, []);
  return (
    <button onClick={() => navigate('/notificaciones')} title="Notificaciones" aria-label="Notificaciones"
      style={{ position: 'relative', background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, color: 'rgba(255,255,255,0.82)', display: 'inline-flex' }}>
      <Bell size={18} />
      {sinLeer > 0 && (
        <span style={{ position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8,
          background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {sinLeer > 9 ? '9+' : sinLeer}
        </span>
      )}
    </button>
  );
}

/* Activar los avisos en este dispositivo */
function BotonAvisos() {
  const [estado, setEstado] = useState('cargando');
  useEffect(() => { avisosActivos().then(a => setEstado(a ? 'activos' : 'inactivos')).catch(() => setEstado('inactivos')); }, []);
  if (estado === 'cargando') return null;
  if (estado === 'activos') return <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 600 }}>Avisos activados en este dispositivo</span>;
  const activar = async () => {
    const r = await activarAvisos(api).catch(() => 'error');
    if (r === 'granted') { setEstado('activos'); toast.success('Listo, te avisamos al instante'); }
    else if (r === 'instalar_primero') toast('Agregá el portal a tu pantalla de inicio y después activalos', { duration: 6000 });
    else if (r === 'denied') toast.error('Quedaron bloqueados. Se habilitan desde los ajustes del navegador.', { duration: 7000 });
    else toast.error('Este dispositivo no admite avisos');
  };
  return <button className="btn btn-secondary btn-sm" onClick={activar}><Bell size={14} /> Activar avisos</button>;
}

/* Avisos de la cuenta */
function Notificaciones() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get('/notificaciones').then(r => { setD(r.data); api.put('/notificaciones/leidas').catch(() => {}); }).catch(() => {});
  }, []);
  if (!d) return <div style={{ color: 'var(--text-muted)' }}>Cargando...</div>;
  return (
    <div>
      <Migas camino={[{ label: 'Inicio', to: '/' }, { label: 'Notificaciones' }]} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800 }}>Notificaciones</h1>
        <BotonAvisos />
      </div>
      {d.notificaciones.length ? d.notificaciones.map(n => (
        <div key={n.id} className="card" style={{ marginBottom: 10, cursor: n.url ? 'pointer' : 'default', borderLeft: n.leida_at ? '1px solid var(--border)' : '3px solid var(--accent)' }}
          onClick={() => n.url && navigate(n.url)}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{n.titulo}</div>
          {n.cuerpo && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{n.cuerpo}</div>}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{fecha(n.created_at)}</div>
        </div>
      )) : <p style={{ color: 'var(--text-muted)' }}>Todavía no tenés novedades.</p>}
    </div>
  );
}

/* ---------------- Modal de pago ---------------- */
function ModalPago({ cobro, onClose }) {
  const [medios, setMedios] = useState([]);
  const [form, setForm] = useState({ fecha: new Date().toISOString().slice(0, 10), monto: cobro.monto, notas: '' });
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { api.get('/medios-pago').then(r => setMedios(r.data)).catch(() => {}); }, []);

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (archivo) fd.append('comprobante', archivo);
      await api.post(`/cobros/${cobro.id}/informar-pago`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('¡Gracias! Registramos tu aviso de pago');
      onClose(true);
    } catch { toast.error('No se pudo registrar'); }
    finally { setEnviando(false); }
  };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h2>Informar un pago</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '6px 0 14px' }}>{cobro.concepto} · {money(cobro.monto)}</p>
        {medios.length > 0 && (
          <div style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Datos para transferir</div>
            {medios.map(m => <div key={m.id} style={{ marginBottom: 4 }}><b>{m.nombre}</b>{m.datos ? <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{m.datos}</div> : null}</div>)}
          </div>
        )}
        <form onSubmit={enviar}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label>Fecha del pago</label><input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} required /></div>
            <div className="form-group"><label>Monto</label><input type="number" step="0.01" value={form.monto} onChange={e => setForm({ ...form, monto: e.target.value })} required /></div>
          </div>
          <div className="form-group"><label>Comprobante (opcional)</label>
            <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}><Upload size={14} /> {archivo ? archivo.name.slice(0, 26) : 'Subir archivo'}
              <input type="file" accept="image/*,.pdf" onChange={e => setArchivo(e.target.files[0])} style={{ display: 'none' }} /></label>
          </div>
          <div className="form-group"><label>Comentario (opcional)</label><input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={enviando}><CheckCircle2 size={16} /> {enviando ? 'Enviando...' : 'Avisar el pago'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Cambiar contraseña ---------------- */
function ModalPassword({ onClose }) {
  const [f, setF] = useState({ actual: '', nueva: '', repetir: '' });
  const guardar = async (e) => {
    e.preventDefault();
    if (f.nueva !== f.repetir) return toast.error('Las contraseñas no coinciden');
    try { await api.put('/password', f); toast.success('Contraseña actualizada'); onClose(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <h2>Cambiar contraseña</h2>
        <form onSubmit={guardar}>
          <div className="form-group"><label>Contraseña actual</label><input type="password" value={f.actual} onChange={e => setF({ ...f, actual: e.target.value })} required /></div>
          <div className="form-group"><label>Nueva (mínimo 8)</label><input type="password" minLength={8} value={f.nueva} onChange={e => setF({ ...f, nueva: e.target.value })} required /></div>
          <div className="form-group"><label>Repetir</label><input type="password" value={f.repetir} onChange={e => setF({ ...f, repetir: e.target.value })} required /></div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 12 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Si algo se rompe al pintar, mostramos un aviso en vez de una pantalla vacía */
class Limite extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error) { console.error('portal:', error); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <PantallaAcceso titulo="Algo no cargó bien" bajada="Probá recargar la página. Si sigue pasando, escribinos y lo revisamos.">
        <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: 12 }} onClick={() => window.location.reload()}>Recargar</button>
        <button className="btn btn-ghost btn-sm" style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
          onClick={() => { localStorage.removeItem('portal_token'); window.location.href = '/'; }}>Salir de la sesión</button>
      </PantallaAcceso>
    );
  }
}

/* Textos legales: el marco de la relación, en criollo */
function Legales() {
  const [ver, setVer] = useState(null);
  const bloque = { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.75 };
  const h = { fontSize: 14, fontWeight: 700, margin: '16px 0 6px', color: 'var(--text-primary)' };

  const TEXTOS = {
    privacidad: (
      <div style={bloque}>
        <p>Guardamos solo lo necesario para prestarte el servicio: tus datos de contacto, los de facturación,
        el detalle de lo que contrataste y los pagos. Nada más.</p>
        <div style={h}>Para qué los usamos</div>
        <p>Para emitir tus facturas, avisarte de vencimientos y novedades de tus proyectos, y darte soporte.
        No vendemos ni cedemos tus datos a terceros con fines comerciales.</p>
        <div style={h}>Con quién los compartimos</div>
        <p>Solo con los servicios que hacen funcionar la plataforma: el hosting donde vive este portal y el
        proveedor de correo con el que te escribimos. Cada uno accede únicamente a lo que necesita.</p>
        <div style={h}>Tus derechos</div>
        <p>Podés pedirnos en cualquier momento una copia de tus datos, corregirlos o darlos de baja.
        Escribinos a <a href="mailto:contacto@udream.com.ar">contacto@udream.com.ar</a> y lo resolvemos.</p>
        <div style={h}>Cuánto los conservamos</div>
        <p>Mientras seas cliente y, después, el tiempo que exigen las normas fiscales para la documentación
        de facturación.</p>
      </div>
    ),
    terminos: (
      <div style={bloque}>
        <p>Este portal es una herramienta para que veas el estado de tus servicios, tus pagos y el avance de
        tus proyectos. Es informativo: lo que vale es lo que acordamos en cada propuesta.</p>
        <div style={h}>Los pagos</div>
        <p>Cuando informás un pago desde el portal, lo estás avisando: queda registrado como informado y lo
        confirmamos cuando verificamos que ingresó. El portal no procesa cobros.</p>
        <div style={h}>Tu acceso</div>
        <p>Tu usuario es personal. Si alguien más de tu equipo necesita entrar, pedinos un acceso propio en
        lugar de compartir el tuyo.</p>
        <div style={h}>Los servicios</div>
        <p>Los importes, plazos y alcances son los de la propuesta que aceptaste. Los abonos se renuevan mes a
        mes y los podés dar de baja avisándonos; las renovaciones anuales vencen el último día del mes
        correspondiente.</p>
        <div style={h}>Disponibilidad</div>
        <p>Hacemos lo posible para que el portal esté siempre disponible, pero puede haber interrupciones por
        mantenimiento o por causas ajenas a nosotros.</p>
      </div>
    ),
  };

  return (
    <>
      <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ cursor: 'pointer' }} onClick={() => setVer('privacidad')}>Política de privacidad</span>
        <span>·</span>
        <span style={{ cursor: 'pointer' }} onClick={() => setVer('terminos')}>Términos del servicio</span>
      </div>
      {ver && (
        <div className="modal-overlay" onClick={() => setVer(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>{ver === 'privacidad' ? 'Política de privacidad' : 'Términos del servicio'}</h2>
            {TEXTOS[ver]}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 18 }}>
              Última actualización: septiembre de 2026 · Udream · udream.com.ar
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-primary" onClick={() => setVer(null)}>Entendido</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* Pie con los datos de contacto, para que el cliente sepa a dónde escribir */
const SOPORTE_MAIL = 'contacto@udream.com.ar';
const SOPORTE_WPP = '541140996263';
function PieSoporte() {
  return (
    <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--border)', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.7 }}>
      ¿Necesitás una mano con tus proyectos o con el uso de la plataforma? Escribinos:{' '}
      <a href={`mailto:${SOPORTE_MAIL}`}>{SOPORTE_MAIL}</a>
      {' · '}
      <a href={`https://wa.me/${SOPORTE_WPP}`} target="_blank" rel="noopener noreferrer">WhatsApp 11 4099 6263</a>
      <div style={{ fontSize: 11, marginTop: 8 }}>Udream App · udream.com.ar</div>
      <Legales />
    </div>
  );
}

/* ---------------- App del portal ---------------- */
export default function PortalApp() {
  return <Limite><PortalInterno /></Limite>;
}

function PortalInterno() {
  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagar, setPagar] = useState(null);
  const [cambiarPass, setCambiarPass] = useState(false);
  const [recargar, setRecargar] = useState(0);
  const location = useLocation();
  const [tema, setTema] = useState(leerTema);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    aplicarTema(leerTema());
    if (!localStorage.getItem('portal_token')) { setCargando(false); return; }
    api.get('/inicio').then(r => setUsuario(r.data.usuario)).catch(() => localStorage.removeItem('portal_token')).finally(() => setCargando(false));
  }, []);

  const salir = () => { localStorage.removeItem('portal_token'); setUsuario(null); };

  if (location.pathname === '/recuperar') return <Recuperar />;
  if (cargando) return <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>Cargando...</div>;
  if (!usuario) return <Login onEntrar={setUsuario} />;

  const link = ({ isActive }) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 10, fontSize: 14,
    fontWeight: isActive ? 700 : 500, color: isActive ? '#0B1C33' : 'rgba(255,255,255,0.82)',
    background: isActive ? '#fff' : 'rgba(255,255,255,0.10)', textDecoration: 'none',
  });

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <header style={{ background: DEGRADE, boxShadow: '0 6px 24px rgba(11,28,51,0.18)', position: 'sticky', top: 0, zIndex: 1200 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="portal-hamburguesa" onClick={() => setMenu(true)} aria-label="Menú"
            style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, display: 'none' }}>
            <Menu size={22} />
          </button>
          <img src={usuario.logo || iconoDelEntorno()} alt="" onError={e => { e.target.src = iconoDelEntorno(); }}
            style={{ width: 38, height: 38, borderRadius: 11, border: '1px solid rgba(255,255,255,0.85)', objectFit: 'cover', background: '#fff' }} />
          <div style={{ marginRight: 'auto', minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.15, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{usuario.cliente}</div>
            <div className="portal-firma" style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', letterSpacing: 0.4 }}>Portal de clientes · Udream App</div>
          </div>
          <nav className="portal-nav" style={{ display: 'flex', gap: 4 }}>
            <NavLink to="/" end style={link}><Home size={16} /> Inicio</NavLink>
            {usuario.ve_importes && <NavLink to="/facturas" style={link}><Receipt size={16} /> Facturas</NavLink>}
            <NavLink to="/proyectos" style={link}><FolderKanban size={16} /> Mis servicios</NavLink>
          </nav>
          <Campanita />
          <ThemeToggle theme={tema} onChange={t => { aplicarTema(t); setTema(t); }} color="rgba(255,255,255,0.82)" />
          <NavLink to="/cuenta" title="Mi cuenta"
            style={({ isActive }) => ({ color: isActive ? '#fff' : 'rgba(255,255,255,0.82)', display: 'inline-flex', padding: 6 })}>
            <UserCircle size={19} />
          </NavLink>
          <button className="portal-solo-pc btn-sobre-degrade" onClick={salir}><LogOut size={15} /> Salir</button>
        </div>
      </header>

      {/* Cajón lateral del celular */}
      {menu && (
        <>
          <div onClick={() => setMenu(false)} style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 1800 }} />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: 268, maxWidth: '82vw', zIndex: 1900,
            background: DEGRADE, padding: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <img src={usuario.logo || iconoDelEntorno()} alt="" onError={e => { e.target.src = iconoDelEntorno(); }}
                style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid rgba(255,255,255,0.85)', objectFit: 'cover', background: '#fff' }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{usuario.cliente}</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{usuario.nombre || usuario.email}</div>
              </div>
              <button onClick={() => setMenu(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>
            <NavLink to="/" end style={link} onClick={() => setMenu(false)}><Home size={17} /> Inicio</NavLink>
            {usuario.ve_importes && <NavLink to="/facturas" style={link} onClick={() => setMenu(false)}><Receipt size={17} /> Facturas</NavLink>}
            <NavLink to="/proyectos" style={link} onClick={() => setMenu(false)}><FolderKanban size={17} /> Mis servicios</NavLink>
            <NavLink to="/minutas" style={link} onClick={() => setMenu(false)}><FileText size={17} /> Minutas</NavLink>
            <NavLink to="/notificaciones" style={link} onClick={() => setMenu(false)}><Bell size={17} /> Notificaciones</NavLink>
            <NavLink to="/cuenta" style={link} onClick={() => setMenu(false)}><UserCircle size={17} /> Mi cuenta</NavLink>
            <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <button className="btn-sobre-degrade" style={{ justifyContent: 'flex-start', width: '100%' }}
                onClick={salir}><LogOut size={16} /> Salir</button>
            </div>
          </div>
        </>
      )}

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 22px 90px' }} key={recargar}>
        <Routes>
          <Route path="/" element={<Inicio onPagar={setPagar} />} />
          <Route path="/facturas" element={<Facturas onPagar={setPagar} />} />
          <Route path="/proyectos" element={<Trabajos />} />
          <Route path="/proyectos/:id" element={<DetalleProyecto />} />
          <Route path="/servicios/:id" element={<DetalleServicio />} />
          <Route path="/notificaciones" element={<Notificaciones />} />
          <Route path="/minutas" element={<Minutas />} />
          <Route path="/servicios" element={<Servicios />} />
          <Route path="/cuenta" element={<MiCuenta onCambiarPass={() => setCambiarPass(true)} onSalir={salir} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <PieSoporte />
      </main>

      <AppMovil api={api} />
      {pagar && <ModalPago cobro={pagar} onClose={(ok) => { setPagar(null); if (ok) setRecargar(r => r + 1); }} />}
      {cambiarPass && <ModalPassword onClose={() => setCambiarPass(false)} />}
    </div>
  );
}
